import { useState, useRef, useEffect } from 'react';
import type { PermissionLevel } from '../../api';

const PERMISSION_OPTIONS = [
  { value: 'viewer' as PermissionLevel, label: 'Viewer', description: 'Read-only access' },
  { value: 'owner' as PermissionLevel, label: 'Owner', description: 'Full access' },
] as const;

interface PermissionSelectorProps {
  currentPermission: PermissionLevel | null;
  isUpdating: boolean;
  onPermissionChange: (newPermission: PermissionLevel | null) => void;
  disabled?: boolean;
}

export function PermissionSelector({ currentPermission, isUpdating, onPermissionChange, disabled }: PermissionSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (permission: PermissionLevel | null) => {
    if (permission !== currentPermission) {
      onPermissionChange(permission);
    }
    setIsOpen(false);
  };

  const isDisabled = isUpdating || disabled;
  const currentLabel = currentPermission
    ? PERMISSION_OPTIONS.find(o => o.value === currentPermission)?.label || currentPermission
    : 'None';

  return (
    <div className="role-selector" ref={dropdownRef}>
      <button
        className={`role-selector-trigger ${currentPermission === 'owner' ? 'admin' : 'user'}`}
        onClick={() => !isDisabled && setIsOpen(!isOpen)}
        disabled={isDisabled}
      >
        <span className="role-label">{isUpdating ? 'Updating...' : currentLabel}</span>
        <svg className="chevron" width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      {isOpen && (
        <div className="role-selector-dropdown">
          {PERMISSION_OPTIONS.map((option) => (
            <button
              key={option.value}
              className={`role-option ${option.value === currentPermission ? 'selected' : ''}`}
              onClick={() => handleSelect(option.value)}
            >
              <div className="role-option-content">
                <span className="role-option-label">{option.label}</span>
                <span className="role-option-description">{option.description}</span>
              </div>
              {option.value === currentPermission && (
                <svg className="check-icon" width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M11.5 4L5.5 10L2.5 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </button>
          ))}
          <button
            className={`role-option ${currentPermission === null ? 'selected' : ''}`}
            onClick={() => handleSelect(null)}
          >
            <div className="role-option-content">
              <span className="role-option-label">None</span>
              <span className="role-option-description">Remove access</span>
            </div>
            {currentPermission === null && (
              <svg className="check-icon" width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M11.5 4L5.5 10L2.5 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
